import { createTheme, alpha, ThemeOptions, buttonClasses } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

// COLORS /////////////////////////////////////////////////////////////////////

const colors = {
    // Neutral
    neutral200: "#EEF1F6",
    neutral100: "#FFFFFF",

    // Aqua
    aqua700: "#4EB6B6",
    aqua100: "#D5E5DB",

    // Primary
    primary500: "#4A8468",
    primary200: "#D5E5DB",
    primary100: "#F1EEE3",

    // Coral
    coral900: "#D32F2F",
    coral400: "#F5AFA1",
    coral300: "#F2C3B9",
    coral200: "#FFEBEE",
    coral100: "#FEF6EC",

    // Grey
    grey900: "#000000",
    grey800: "#313439",
    grey700: "#535353",
    grey400: alpha("#535353", 0.5),
    grey300: alpha("#535353", 0.3),
    grey200: alpha("#535353", 0.12),
    grey100: alpha("#535353", 0.06),
};

const borders = {
    default: `1px solid ${colors.grey400}`,
};

const borderRadiuses = {
    default: 20,
    small: 10,
    large: 100,
    circular: "50%",
};

const shadows = {
    default: "0px 0px 15px rgba(0, 0, 0, 0.14)",
    darker: "0 0 15px rgba(0, 0, 0, 0.25)",
    spread: "rgba(24, 24, 24, 0.5) 0px 10px 40px -10px",
};

type Colors = typeof colors;
type Borders = typeof borders;
type BorderRadiuses = typeof borderRadiuses;
type Shadows = typeof shadows;

declare module "@mui/material/styles/createPalette" {
    interface Palette extends Colors {}
    interface PaletteOptions extends Colors {}

    interface TypeBackground {
        contrast: string;
    }
}

declare module "@mui/material/styles" {
    interface Theme {
        border: Borders;
        borderRadius: BorderRadiuses;
        shadow: Shadows;
    }
    interface ThemeOptions {
        border?: Borders;
        borderRadius?: BorderRadiuses;
        shadow?: Shadows;
    }
}

const defaultTheme = createTheme({
    palette: {
        ...colors,

        primary: {
            main: colors.aqua700,
        },
        secondary: {
            main: colors.grey700,
            dark: colors.grey800,
        },
        text: {
            primary: colors.grey700,
            secondary: colors.grey400,
        },
        background: {
            default: colors.coral100,
            contrast: colors.grey100,
        },
        divider: colors.grey200,
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 960,
            lg: 1272, // 1440 - 200 margins on each side in design + 32 minimum margin
            xl: 1720,
        },
    },
    shape: {
        borderRadius: borderRadiuses.default,
    },
    border: borders,
    borderRadius: borderRadiuses,
    shadow: shadows,
});

declare module "@mui/material/Button" {
    interface ButtonPropsVariantOverrides {
        primary: true;
        primaryInverse: true;
        secondary: true;
        secondaryInverse: true;
        tertiary: true;
        tertiaryInverse: true;
        destructive: true;
        destructiveInverse: true;
    }
}

declare module "@mui/material/Chip" {
    interface ChipPropsVariantOverrides {
        pink: true;
        blue: true;
        lightGrey: true;
        darkGrey: true;
        green: true;
    }
}

// TYPOGRAPHY /////////////////////////////////////////////////////////////////

export const titleFont = ["Recoleta", "serif"].join(",");

export const bodyFont = ['"Nunito Sans"', "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(
    ",",
);

const themeOptions: ThemeOptions = {
    typography: {
        // default base font
        fontFamily: bodyFont,
        fontSize: 16,

        h1: {
            fontFamily: titleFont,
            fontWeight: 700,
            fontSize: defaultTheme.typography.pxToRem(34),

            lineHeight: 1.6,

            marginBottom: defaultTheme.spacing(2),

            [defaultTheme.breakpoints.down("sm")]: {
                fontSize: defaultTheme.typography.pxToRem(32),
                marginBottom: defaultTheme.spacing(1),
            },
        },
        h2: {
            fontFamily: titleFont,
            fontWeight: 700,
            fontSize: defaultTheme.typography.pxToRem(28),

            lineHeight: 1.6,

            marginBottom: defaultTheme.spacing(0.5),

            [defaultTheme.breakpoints.down("sm")]: {
                fontSize: defaultTheme.typography.pxToRem(26),
                marginBottom: defaultTheme.spacing(0.5),
            },
        },
        h3: {
            fontFamily: titleFont,
            fontWeight: 600,
            fontSize: defaultTheme.typography.pxToRem(22),

            lineHeight: 1.6,

            marginBottom: defaultTheme.spacing(0.5),

            [defaultTheme.breakpoints.down("sm")]: {
                fontSize: defaultTheme.typography.pxToRem(20),
                marginBottom: defaultTheme.spacing(0.5),
            },
        },
        h4: {
            fontFamily: titleFont,
            fontWeight: 600,
            fontSize: defaultTheme.typography.pxToRem(18),

            lineHeight: 1.6,
        },
        h5: {
            fontFamily: titleFont,
            fontWeight: 600,
            fontSize: defaultTheme.typography.pxToRem(16),

            lineHeight: 1.6,
        },

        h6: {
            fontFamily: titleFont,
            fontWeight: 500,
            fontSize: defaultTheme.typography.pxToRem(16),

            lineHeight: 1.6,
        },
        subtitle1: {
            fontFamily: titleFont,
            fontWeight: 400,
            fontSize: defaultTheme.typography.pxToRem(18),

            lineHeight: 1.6,

            marginBottom: defaultTheme.spacing(0.5),
        },

        subtitle2: {
            fontFamily: titleFont,
            fontWeight: 400,
            fontSize: defaultTheme.typography.pxToRem(16),

            lineHeight: 1.6,

            marginBottom: defaultTheme.spacing(0.5),
        },

        body1: {
            fontFamily: bodyFont,
            fontWeight: 400,
            fontSize: defaultTheme.typography.pxToRem(18),

            lineHeight: 1.6,

            marginBottom: defaultTheme.spacing(2),

            [defaultTheme.breakpoints.down("sm")]: {
                fontSize: defaultTheme.typography.pxToRem(16),
            },
        },

        body2: {
            fontFamily: bodyFont,
            fontWeight: 400,
            fontSize: defaultTheme.typography.pxToRem(20),

            lineHeight: 1.6,

            marginBottom: defaultTheme.spacing(1),

            [defaultTheme.breakpoints.down("sm")]: {
                fontSize: defaultTheme.typography.pxToRem(18),
            },
        },

        button: {
            fontFamily: bodyFont,
            fontWeight: 700,
            fontSize: defaultTheme.typography.pxToRem(18),

            lineHeight: 1.6,

            textTransform: "none",

            [defaultTheme.breakpoints.down("sm")]: {
                fontSize: defaultTheme.typography.pxToRem(16),
            },
        },

        caption: {
            fontFamily: bodyFont,
            fontWeight: 400,
            fontSize: defaultTheme.typography.pxToRem(14),

            lineHeight: 1.6,

            marginBottom: defaultTheme.spacing(0.5),
        },

        overline: {
            fontFamily: bodyFont,
            fontWeight: 500,
            fontSize: defaultTheme.typography.pxToRem(16),

            lineHeight: 1.6,

            marginBottom: defaultTheme.spacing(0.5),

            textTransform: "uppercase",

            [defaultTheme.breakpoints.down("sm")]: {
                fontSize: defaultTheme.typography.pxToRem(14),
            },
        },
    },
    // CUSTOM STYLES FOR COMPONENTS ///////////////////////////////////////////
    components: {
        MuiButton: {
            variants: [
                {
                    props: { variant: "primary" },
                    style: {
                        background: defaultTheme.palette.primary500,
                        color: defaultTheme.palette.neutral100,

                        "&:hover": {
                            background: defaultTheme.palette.grey800,
                        },

                        [`&.${buttonClasses.disabled}`]: {
                            color: defaultTheme.palette.neutral100,
                        },
                    },
                },
                {
                    props: { variant: "primaryInverse" },
                    style: {
                        background: defaultTheme.palette.coral100,
                        color: defaultTheme.palette.grey800,

                        "&:hover": {
                            background: defaultTheme.palette.grey400,
                            color: defaultTheme.palette.neutral100,
                        },

                        [`&.${buttonClasses.disabled}`]: {
                            color: defaultTheme.palette.grey700,
                        },
                    },
                },
                {
                    props: { variant: "secondary" },
                    style: {
                        background: defaultTheme.palette.grey200,
                        color: defaultTheme.palette.grey700,

                        "&:hover": {
                            background: defaultTheme.palette.grey300,
                        },
                    },
                },
                {
                    props: { variant: "secondaryInverse" },
                    style: {
                        background: defaultTheme.palette.grey400,
                        color: defaultTheme.palette.neutral100,

                        "&:hover": {
                            background: defaultTheme.palette.grey300,
                            color: defaultTheme.palette.neutral100,
                        },

                        [`&.${buttonClasses.disabled}`]: {
                            color: defaultTheme.palette.neutral100,
                            background: defaultTheme.palette.grey200,
                        },
                    },
                },
                {
                    props: { variant: "tertiary" },
                    style: {
                        color: defaultTheme.palette.primary500,
                        padding: 0,

                        "&:hover": {
                            textDecoration: "underline",
                            background: "none",
                        },
                        [`&.${buttonClasses.disabled}`]: {
                            color: defaultTheme.palette.grey700,
                        },
                    },
                },
                {
                    props: { variant: "tertiaryInverse" },
                    style: {
                        color: defaultTheme.palette.neutral100,
                        padding: 0,

                        "&:hover": {
                            textDecoration: "underline",
                            background: "none",
                        },
                        [`&.${buttonClasses.disabled}`]: {
                            color: defaultTheme.palette.neutral100,
                        },
                    },
                },
                {
                    props: { variant: "destructive" },
                    style: {
                        background: defaultTheme.palette.coral900,
                        color: defaultTheme.palette.neutral100,

                        "&:hover": {
                            background: defaultTheme.palette.grey800,
                        },

                        [`&.${buttonClasses.disabled}`]: {
                            color: defaultTheme.palette.neutral100,
                        },
                    },
                },
                {
                    props: { variant: "destructiveInverse" },
                    style: {
                        background: defaultTheme.palette.coral900,
                        color: defaultTheme.palette.neutral100,

                        "&:hover": {
                            background: defaultTheme.palette.grey800,
                        },

                        [`&.${buttonClasses.disabled}`]: {
                            color: defaultTheme.palette.neutral100,
                        },
                    },
                },
            ],
            styleOverrides: {
                root: {
                    [`&.${buttonClasses}`]: {
                        ...defaultTheme.typography.button,
                    },
                    padding: defaultTheme.spacing(2, 4),
                    borderRadius: defaultTheme.borderRadius.large,
                    zIndex: defaultTheme.zIndex.speedDial,

                    [`&.${buttonClasses.disabled}`]: {
                        opacity: 0.3,
                    },
                },
                sizeSmall: {
                    padding: defaultTheme.spacing(1, 4),
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                colorPrimary: {
                    background: defaultTheme.palette.primary500,
                    color: defaultTheme.palette.neutral100,

                    "&:hover": {
                        background: defaultTheme.palette.grey800,
                    },

                    [`&.${buttonClasses.disabled}`]: {
                        color: defaultTheme.palette.neutral100,
                    },
                },
                colorSecondary: {
                    background: "none",
                    color: defaultTheme.palette.grey700,

                    "&:hover": {
                        background: defaultTheme.palette.grey200,
                    },

                    [`&.${buttonClasses.disabled}`]: {
                        color: defaultTheme.palette.grey700,
                        opacity: 0.3,
                    },
                },
                sizeSmall: {
                    padding: defaultTheme.spacing(1),
                },
                sizeMedium: {
                    padding: defaultTheme.spacing(1.75),
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    marginBottom: 0,
                },
                strong: {
                    fontWeight: 700,
                },
            },
        },
        MuiLink: {
            defaultProps: {
                underline: "hover",
                fontWeight: 700,
            },
            styleOverrides: {
                root: {
                    cursor: "pointer",
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    "&.Mui-checked": {
                        color: defaultTheme.palette.grey700,
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                colorPrimary: {
                    "&.Mui-checked": {
                        color: defaultTheme.palette.aqua700,
                    },
                },
                colorSecondary: {
                    "&.Mui-checked": {
                        color: defaultTheme.palette.grey700,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            defaultProps: {
                rows: 4,
            },
            styleOverrides: {
                root: {
                    borderRadius: defaultTheme.borderRadius.small,
                    backgroundColor: defaultTheme.palette.coral100,
                    "&.Mui-error": {
                        background: defaultTheme.palette.coral200,
                    },
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                asterisk: {
                    color: defaultTheme.palette.coral900,
                },
            },
        },
        MuiSelect: {
            defaultProps: {
                IconComponent: ExpandMoreRoundedIcon,
            },
        },
        MuiMenu: {
            styleOverrides: {
                list: {
                    backgroundColor: defaultTheme.palette.coral100,
                    borderRadius: defaultTheme.borderRadius.default,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    margin: 0,
                    "&:hover": {
                        backgroundColor: defaultTheme.palette.grey200,
                    },
                    "&.Mui-selected": {
                        backgroundColor: defaultTheme.palette.grey100,
                        "&:hover": {
                            backgroundColor: defaultTheme.palette.grey200,
                        },
                    },
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    marginBottom: 0,
                },
            },
        },
        MuiChip: {
            variants: [
                {
                    props: { variant: "pink" },
                    style: {
                        background: defaultTheme.palette.coral200,
                        color: defaultTheme.palette.coral400,
                    },
                },
                {
                    props: { variant: "blue" },
                    style: {
                        background: defaultTheme.palette.aqua100,
                        color: defaultTheme.palette.aqua700,
                    },
                },
                {
                    props: { variant: "lightGrey" },
                    style: {
                        background: defaultTheme.palette.grey200,
                        color: defaultTheme.palette.grey700,
                    },
                },
                {
                    props: { variant: "darkGrey" },
                    style: {
                        background: defaultTheme.palette.grey400,
                        color: defaultTheme.palette.neutral100,
                    },
                },
                {
                    props: { variant: "green" },
                    style: {
                        background: defaultTheme.palette.primary500,
                        color: defaultTheme.palette.neutral100,
                    },
                },
            ],
            styleOverrides: {
                root: {
                    borderRadius: defaultTheme.borderRadius.large,
                    width: "fit-content",
                    height: "fit-content",
                },
                label: {
                    padding: 0,
                },
                icon: {
                    margin: 0,
                    paddingRight: defaultTheme.spacing(1),
                },
                sizeSmall: {
                    ...defaultTheme.typography.caption,
                    padding: defaultTheme.spacing(0.5, 2),
                },
                sizeMedium: {
                    ...defaultTheme.typography.body1,
                    padding: defaultTheme.spacing(1.75, 2),
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                outlinedInfo: {
                    borderColor: defaultTheme.palette.aqua700,

                    "& .MuiAlert-icon": {
                        color: defaultTheme.palette.aqua700,
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundColor: defaultTheme.palette.background.default,
                    borderRadius: defaultTheme.borderRadius.default,
                },
                paperFullScreen: {
                    borderRadius: 0,
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                rounded: {
                    borderRadius: defaultTheme.borderRadius.default,
                },
            },
        },
        MuiSnackbar: {
            styleOverrides: {
                root: {
                    zIndex: defaultTheme.zIndex.modal - 1,
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    backgroundColor: defaultTheme.palette.coral100,
                    boxShadow: "none",
                    "&.Mui-expanded": {
                        margin: 0,
                        "&:before": {
                            opacity: "1",
                        },
                    },
                    "&:first-child::after": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: 0,
                        height: 1,
                        backgroundColor: defaultTheme.palette.grey200,
                    },
                    "&:last-child:after": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        bottom: 0,
                        right: 0,
                        height: 1,
                        backgroundColor: defaultTheme.palette.grey200,
                    },
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    minHeight: 0,
                    "&.Mui-expanded": {
                        minHeight: 0,
                    },
                },
                content: {
                    margin: defaultTheme.spacing(1),
                    "&.Mui-expanded": {
                        margin: defaultTheme.spacing(1),
                    },
                    "& .MuiTypography-root": {
                        margin: 0,
                    },
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    paddingTop: 0,
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    margin: defaultTheme.spacing(3, 0),
                },
            },
        },
    },
};

export const theme = createTheme(defaultTheme, themeOptions);
